import {Component, inject, DestroyRef, OnInit} from '@angular/core';
import {DxTabsComponent} from "devextreme-angular";
import {EmployeeProfileComponent} from "../employee-profile/employee-profile.component";
import {MyScoreComponent} from "../my-score/my-score.component";
import {ActivatedRoute, Router} from "@angular/router";
import {map, switchMap, shareReplay, take} from "rxjs/operators";
import {distinctUntilChanged, Observable, BehaviorSubject, combineLatest, of} from "rxjs";
import {AsyncPipe} from "@angular/common";
import {AccessType, AuthService, PermissionType, UsersClient} from "@services";
import {ScheduleComponent} from "../schedule/schedule.component";
import {TimeLogsComponent} from "../time-logs/time-logs.component";
import {TimeOffTabsComponent} from "../time-off/time-off-tabs/time-off-tabs.component";
import {ChangeRequestsComponent} from "../change-requests/change-requests.component";
import {UserHistoryComponent} from "../user-history/user-history.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {UserRole} from "../../../shared/constants";

type TabKey = 'my-score' | 'profile' | 'schedule' | 'time-logs' | 'time-off' | 'request-forms' | 'history';

interface Tab {
    key: TabKey,
    text: string,
    icon: string,
    permission?: PermissionType | PermissionType[],
    employeeOnly?: boolean
}

@Component({
    selector: 'app-employee',
    imports: [
        DxTabsComponent,
        EmployeeProfileComponent,
        MyScoreComponent,
        AsyncPipe,
        ScheduleComponent,
        TimeLogsComponent,
        TimeOffTabsComponent,
        ChangeRequestsComponent,
        UserHistoryComponent
    ],
    templateUrl: './employee.component.html'
})
export class EmployeeComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private auth = inject(AuthService);
    private usersClient = inject(UsersClient);
    private destroyRef = inject(DestroyRef);

    public authorized: boolean = false;

    private readonly employeeRoles: UserRole[] = [UserRole.WarehouseEmployee, UserRole.AgencyEmployee, UserRole.OfficeEmployee];

    private readonly allTabs: Tab[] = [
        {key: 'my-score', text: 'My Score', icon: 'fas fa-chart-pie', employeeOnly: true},
        {key: 'profile', text: 'Profile', icon: 'fas fa-user'},
        {
            key: 'schedule',
            text: 'Schedule',
            icon: 'fas fa-calendar-alt',
            permission: PermissionType.Schedule,
            employeeOnly: true
        },
        {
            key: 'time-logs',
            text: 'Time Logs',
            icon: 'fas fa-clock',
            permission: PermissionType.TimeLogs,
            employeeOnly: true
        },
        {
            key: 'time-off', text: 'Time Off', icon: 'fas fa-plane',
            permission:
                [
                    PermissionType.VacationRequests,
                    PermissionType.LieuDayRequests,
                    PermissionType.PersonalTimeRequests,
                    PermissionType.FloaterDayRequests,
                    PermissionType.LeaveOfAbsenceRequests,
                    PermissionType.AgencyTimeOffRequests,
                ],
            employeeOnly: true
        },
        {
            key: 'request-forms',
            text: 'Request Forms',
            icon: 'fas fa-file-alt',
            permission: PermissionType.InformationChangeRequestsList,
            employeeOnly: true
        },
        {key: 'history', text: 'History', icon: 'fas fa-history'},
    ];

    private readonly myScorePermissions: PermissionType[] = [
        PermissionType.MyScoreAttendance,
        PermissionType.MyScoreFinancialDetails,
        PermissionType.MyScoreTrending,
    ];

    // Mirrors which sections employee-profile renders for the target user's roles.
    private readonly othersProfileSections: { permission: PermissionType, roles?: UserRole[] }[] = [
        {permission: PermissionType.UserContactInformationOthers},
        {permission: PermissionType.UserAlternateNamesOthers},
        {permission: PermissionType.UserNotesOthers},
        {permission: PermissionType.UserSecurityAndPermissionsOthers},
        {permission: PermissionType.UserEmergencyDetailsOthers},
        {permission: PermissionType.EmployeeShiftDetailsOthers, roles: this.employeeRoles},
        {permission: PermissionType.EmployeeFinancialDetailsOthers, roles: this.employeeRoles},
        {permission: PermissionType.EmployeeEmploymentDetailsOthers, roles: this.employeeRoles},
        {permission: PermissionType.EmployeeNestleDetailsOthers, roles: this.employeeRoles},
        {permission: PermissionType.UserSupplierDetailsOthers, roles: [UserRole.Supplier]},
        {permission: PermissionType.UserCarrierDetailsOthers, roles: [UserRole.Carrier]},
        {permission: PermissionType.UserVendorDetailsOthers, roles: [UserRole.Vendor]},
        {permission: PermissionType.UserCustomerDetailsOthers, roles: [UserRole.Customer]},
        {permission: PermissionType.UserSiteDetailsOthers, roles: [UserRole.Finance]},
        {permission: PermissionType.UserAgencyDetailsOthers, roles: [UserRole.Agency]},
    ];

    readonly employeeId$ = this.route.paramMap.pipe(
        map(params => params.get('employeeId') ?? this.auth.userId),
        distinctUntilChanged(),
        shareReplay(1)
    );

    private readonly refresh$ = new BehaviorSubject<void>(void 0);
    private readonly localRoles$ = new BehaviorSubject<string[] | null>(null);

    private readonly userRoles$ = combineLatest([this.employeeId$, this.refresh$]).pipe(
        switchMap(([id]) => id ? this.usersClient.getUserRoles(id) : of([])),
        shareReplay(1)
    );

    private readonly effectiveRoles$ = combineLatest([this.userRoles$, this.localRoles$]).pipe(
        map(([serverRoles, localRoles]) => localRoles ?? serverRoles),
        shareReplay(1)
    );

    readonly isEmployee$: Observable<boolean> = this.effectiveRoles$.pipe(
        map(roles => this.hasAnyRole(roles, this.employeeRoles)),
        distinctUntilChanged()
    );

    readonly tabs$ = combineLatest([this.employeeId$, this.effectiveRoles$]).pipe(
        map(([employeeId, roles]) => this.allTabs
            .filter(t => this.isAuthorized(t, employeeId, roles))
            .map(t => ({key: t.key, text: t.text, icon: t.icon}))
        ),
        shareReplay(1)
    );

    selectedTab: TabKey | null = null;

    constructor() {
        this.tabs$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(tabs => {
            this.selectedTab = tabs[0]?.key ?? null;
        });
    }

    async ngOnInit(): Promise<void> {
        const employeeId = this.route.snapshot.paramMap.get('employeeId');

        this.authorized = employeeId == null
            || this.auth.userId == employeeId 
            || this.auth.checkPermission(PermissionType.UserList, AccessType.Edit)
            || this.auth.canViewOtherEmployees(PermissionType.EmployeesList);

        if (!this.authorized) {
            await this.router.navigate(['/my-score']);
        }
    }

    onProfileSaved() {
        this.localRoles$.next(null);
        this.refresh$.next();
    }

    private isAuthorized(item: Tab, employeeId: string | null, roles: string[]): boolean {
        if (item.employeeOnly && !this.hasAnyRole(roles, this.employeeRoles)) {
            return false;
        }
        if (item.key === 'my-score')
            return this.myScorePermissions.some(p => this.auth.canViewMyScoreSection(employeeId, p));
        if (item.key === 'history')
            return this.auth.canViewProfileSection(employeeId, PermissionType.UserProfileHistoryOwn, PermissionType.UserProfileHistoryOthers);
        if (item.key === 'profile')
            return this.auth.isOwnProfile(employeeId) || this.othersProfileSections.some(s =>
                (!s.roles || this.hasAnyRole(roles, s.roles)) && this.auth.checkPermission(s.permission, AccessType.ViewOnly));
        if (!item.permission)
            return true;

        const permissions = Array.isArray(item.permission) ? item.permission : [item.permission];
        return permissions.some(p => this.auth.checkPermission(p, AccessType.ViewOnly));
    }

    private hasAnyRole(roles: string[], expected: UserRole[]): boolean {
        return roles.some(r => expected.includes(r as UserRole));
    }
}
